import classNames from 'classnames';
import * as React from 'react';
import type { PictureProfile } from '../../../PictureProfile.csharp';
import type { PosterViewModel } from '../Poster/PosterViewModel.csharp';
import { RowType } from '../Row/RowType.csharp';
import type { RowViewModel } from '~/Blocks/PosterListBlock/Row/RowViewModel.csharp';
import { HtmlDiv } from '~/Partials/Controls/HtmlDiv';
import { HtmlHeading } from '~/Partials/Controls/HtmlHeading';
import { Picture } from '~/Partials/Picture/Picture';
import { PicturePlaceholder } from '~/Partials/Picture/Placeholder/PicturePlaceholder';
import type { StrictOmit } from '~/Partials/Utils/typeUtils';
import styles from './PosterListRow.module.css';
import { getDefaultPictureProfile, getFirstPosterPictureProfile } from './PosterListRowPictureProfilesUtils';

export interface RowWithData extends StrictOmit<RowViewModel, 'itemsCount'> {
    posters: PosterViewModel[];
}

interface PosterListRowProps extends RowWithData {
    reverse?: boolean;
}

export const PosterListRow: React.FC<React.PropsWithChildren<PosterListRowProps>> = props => {
    const { type, posters, reverse } = props;

    const renderPoster = (poster: PosterViewModel, profile?: PictureProfile) => (
        <li className={styles.Poster} key={poster.primaryLinkUrl}>
            <div className={styles.PosterImageOverlay}>
                {poster.image ? (
                    <Picture
                        {...poster.image}
                        profile={profile ?? getDefaultPictureProfile(type)}
                        className={styles.PosterImage}
                    />
                ) : (
                    <PicturePlaceholder className={styles.PosterImage} />
                )}
            </div>
            <div className={styles.PosterBody}>
                {poster.category && <span className={styles.PosterCategory}>{poster.category}</span>}
                {poster.title && <HtmlHeading type="h3" className={styles.PosterTitle} html={poster.title} />}
                {poster.text && <HtmlDiv className={styles.PosterDescription} html={poster.text} />}
            </div>
            <a href={poster.primaryLinkUrl} className="PosterListRow__linkOverlay">
                {poster.title && <HtmlDiv className="visuallyHidden" html={poster.title} />}
            </a>
        </li>
    );

    return (
        <ul
            className={classNames(
                styles.PosterListRow,
                styles[`Variant--${type}`],
                reverse && styles['Variant--reversed']
            )}
        >
            {type === RowType.OneLeftAndTwoOnTwoRight || type === RowType.OneLeftAndFourOnOneRight
                ? posters.map((poster, index) =>
                      renderPoster(poster, index === 0 ? getFirstPosterPictureProfile(type) : undefined)
                  )
                : posters.map(poster => renderPoster(poster))}
        </ul>
    );
};
